import { useEffect, useContext, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextInput from "../../../Common/TextInput/TextInput";
import SelectInput from "../../../Common/Select-Input/SelectInput";
import Button from "../../../Common/Button/Button";
import backArrowIcon from "../../../Assets/Images/arrow-back-icon.png";
import OptionData from "../../../Assets/Json/StaticOptions.json";
import { DynamicContext } from "../../../Context/DynamicContext";
import MonsterConfig from "../MonsterConfig.json";
import MonsterUsaCard from "./MonsterUsaCard";
import compactIcon from "../../../Assets/Images/compact-icon.svg";
import detailedIcon from "../../../Assets/Images/detailed-icon.svg";

import {
  MONSTER_SEARCH_BASE_URL,
  MONSTER_TOKEN_USA_URL,
  MONSTER_SEARCH_USA,
  MONSTER_VIEW_USA,
  UPDATE_CANDIDATE_DETAILS,
  GET_CANDIDATES_BY_NOTES,
  VYLABS_BASE_URL,
  GET_CANDIDATE_BY_ID,
  GET_VENDOR_DETAILS_BY_ID,
  CCREATE_CAN,
  VENDORS_SEARCH,
  LINK_CANDIDATE_RESUME,
} from "../../../helper/url_helper";
import "./MonsterUsaSearch.css";
import { parseMonsterResumeHeadless } from "./MonsterUsaResumeParser";
import CandidateLightIcon from "../../../Assets/Images/candidates-header-icon.svg";
import CandidateDarkIcon from "../../../Assets/Images/candidates-header-darkicon.svg";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import CandidateDetailsContainer from "../../../Common/Candidate-Details-Container/CandidateDetailsContainer";
import SidePopup from "../../../Common/SidePopup/SidePopup";
import AnimationPlayer from "../../../Common/Animation-Player/AnimationPlayer";
import VybogTALLoaderAnimation from "../../../Common/Dynamic-Animations/DynamicAnimations";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import infoIcon from "../../../Assets/Images/infoicon.svg";
import { get, post } from "../../../helper/Api_helper";
import DynamicSkeleton from "../../../Common/Skeleton/Skeleton";
import envConfig from "../../../helper/env_config";
import NoDataFound from "../../../Assets/Json/nodata-found-robot-animation.json";
import DynamicMultipleSelectCheckmarks from "../../Multiple-Select-Checkmarks/MultipleSelectCheckmarks";

function MonsterUsaSearch() {
  const { isDarkMode, isVendorFeatureEnabled } = useContext(DynamicContext);
  const { StaticOptions = {} } = OptionData;
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [cardView, setCardView] = useState("compact");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isProcessingView, setIsProcessingView] = useState(false);
  const [localCandidate, setLocalCandidate] = useState(null);
  const [fallbackMonsterPopupOpen, setFallbackMonsterPopupOpen] =
    useState(false);
  const [selectedMonsterId, setSelectedMonsterId] = useState(null);
  const [lastProfileDetails, setLastProfileDetails] = useState(null);
  const [wsRef, setWsRef] = useState(null);
  const [wsPingInterval, setWsPingInterval] = useState(null);
  const [wsPingTimeout, setWsPingTimeout] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const observer = useRef();
  const navigate = useNavigate();
  const [parsedMap, setParsedMap] = useState(() => {
    try {
      const raw = localStorage.getItem("monsterCandidateIdMap");
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isEditingCandidate, setIsEditingCandidate] = useState(false);
  const [originalCandidateData, setOriginalCandidateData] = useState(null);
  const [editedCandidateData, setEditedCandidateData] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const [vendorOptionLoading, setVendorOptionLoading] = useState(false);
  const [assignedVendor, setAssignedVendor] = useState(null);
  const [hasCandidateId , setHasCandidateId] = useState(false);
  const [realCandidateId, setRealCandidateId] = useState();
  const [isImportFailed, setIsImportFailed] = useState(false);
  const processStatusRef = useRef(null);

  const mapMonsterCandidateToCardProps = (candidate) => {
    const name = candidate?.identity?.name || "Unknown";

    const role =
      candidate?.relevance?.experience?.title?.name || "Not specified";

    const experience =
      candidate?.yearsOfExperience && Number(candidate?.yearsOfExperience) > 0
        ? candidate?.yearsOfExperience
        : 0;

    const skills = Array.isArray(candidate?.relevance?.skills)
      ? candidate.relevance.skills
          .map((skill) => skill?.name || skill)
          .filter((s) => typeof s === "string" && s.trim() !== "")
      : [];

    const location = [
      candidate?.location?.city,
      candidate?.location?.state,
      candidate?.location?.country,
    ]
      .filter(Boolean)
      .join(", ");

    return {
      name,
      role,
      experience,
      skills,
      location,
      workAuthorizations: candidate?.location?.workAuthorizations ?? [],
      willRelocate: candidate?.location?.willRelocate,
      degree: candidate?.degree,
    };
  };

  const toggleAdvancedSearch = () => setShowAdvancedSearch(!showAdvancedSearch);

  const fetchVendors = async (inputValue = "") => {
    try {
      setVendorOptionLoading(true);
      const response = await get(
        VYLABS_BASE_URL,
        `${VENDORS_SEARCH}?vendor_name=${encodeURIComponent(inputValue)}`,
      );
      const { vendors = [] } = response;
      const fetchedVendors = Array.isArray(vendors)
        ? vendors
            .filter((v) => v?.id && v?.name)
            .map((v) => ({
              label: v.contact_person
                ? `${v.name} - ${v.contact_person}`
                : v.name,
              value: v.id,
            }))
        : [];
      setVendorList(fetchedVendors);
    } catch (err) {
      console.error("fetchVendors error", err);
      setVendorList([]);
    } finally {
      setVendorOptionLoading(false);
    }
  };
  const { DistanceUnit = [], DiceCompany = [] } = StaticOptions;

  const createCandidateFromFallback = async (
    monsterId,
    fileContent,
    profile,
    fileID,
  ) => {
    if (!fileContent) {
      console.error("[Monster Fallback] Failed: No file content provided.");
      return null;
    }

    const toStringOrNull = (v) => {
      if (v == null) return null;
      if (Array.isArray(v)) return v.length ? String(v[0]).trim() : null;
      return String(v).trim();
    };
    const rawName = profile?.identity?.name;
    const rawEmail = profile?.identity?.emailAddress;
    const rawPhone = profile?.phoneNumbers?.[0]?.phoneNumberValue || null;
    const city =
      profile?.location?.city || profile?.contact?.address?.[0]?.city || "";
    const state =
      profile?.location?.state || profile?.contact?.address?.[0]?.state || "";
    const country =
      profile?.location?.country ||
      profile?.contact?.address?.[0]?.country ||
      "";
    const designation =
      profile?.relevance?.experience?.title?.name ||
      profile?.current_employment?.[0]?.designation?.text ||
      "";
    const yearsOfExperience = profile?.yearsOfExperience || "N/A";

    const name = toStringOrNull(rawName);
    const email = toStringOrNull(rawEmail);
    let phone = toStringOrNull(rawPhone);
    const formatUSPhone = (phone) => {
      if (!phone) return null;
      const cleaned = phone.replace(/\D/g, "");
      if (cleaned.length === 10) return `+1${cleaned}`;
      if (cleaned.length === 11 && cleaned.startsWith("1"))
        return `+${cleaned}`;
      return null;
    };

    let skills = Array.isArray(profile?.relevance?.skills)
      ? profile.relevance.skills
          .map(
            (s) =>
              s?.name ||
              s?.text ||
              s?.value ||
              (typeof s === "string" ? s : null),
          )
          .filter((s) => typeof s === "string" && s.trim() !== "")
      : [];

    const skillRegex = /<SkillName>(.*?)<\/SkillName>/gi;
    let match;
    while ((match = skillRegex.exec(fileContent)) !== null) {
      const skill = match[1]?.trim();
      if (skill) {
        skills.push(skill);
      }
    }

    skills = [
      ...new Set(skills.map((s) => String(s).trim()).filter(Boolean)),
    ].slice(0, 30);
    if (!name || !email || !phone) {
      return null;
    }

    const formattedPhone = formatUSPhone(rawPhone);
    const appendToCandidateId = "monster_Usa_" + String(monsterId).trim();

    const payload = {
      name,
      email: email,
      phone: formattedPhone,
      city: city,
      state: state,
      country: country,
      skills,
      status: "New Profile",
      source: "Monster US",
      vendorID: envConfig.monsterUsaVendorID,
      notes: appendToCandidateId,
      designation: designation,
      yearsOfExperience: yearsOfExperience,
    };

    if (fileID) {
      payload.fileID = fileID;
    }

    try {
      const response = await post(VYLABS_BASE_URL, CCREATE_CAN, payload);
      if (response?.message === "candidate updated" && response?.id) {
        try {
          await post(VYLABS_BASE_URL, UPDATE_CANDIDATE_DETAILS, {
            id: response.id,
            ...payload,
          });
        } catch (updateError) {
          console.error(
            "[Monster Fallback] Candidate details update failed:",
            updateError,
          );
        }
      }
      return response?.id || null;
    } catch (error) {
      console.error("[Monster Fallback] API ERROR:", error);
      return null;
    }
  };

  const fetchMonsterToken = async () => {
    try {
      const formData = new URLSearchParams();

      const response = await post(
        MONSTER_SEARCH_BASE_URL,
        `${MONSTER_TOKEN_USA_URL}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );

      if (response?.authDetails) {
        const authDetails = response.authDetails;
        const token = authDetails.access_token;
        if (token) {
          localStorage.setItem("monsterTokenUsa", token);
          return token;
        }
        throw new Error("No token found in response");
      }
      throw new Error("No authDetails found in response");
    } catch (err) {
      throw err;
    }
  };

  const isTokenInvalidError = (error) => {
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      JSON.stringify(error.response?.data) ||
      "";
    return (
      error.response?.status === 500 &&
      errorMessage.includes("Token is invalid")
    );
  };

  function htmlToFile(html, fileName) {
    const blob = new Blob([html || ""], { type: "text/html" });
    const file = new File([blob], fileName, { type: "text/html" });
    file.localUrl = URL.createObjectURL(blob);
    return file;
  }
  const htmlToTextFile = htmlToFile;

  function getCandidateFileName(candidateName, fallbackId, extension = "html") {
    const name = candidateName?.trim();
    if (!name || name.toLowerCase() === "unknown name") {
      return `monster_Usa_${fallbackId}.${extension}`;
    }
    const safeName = name.replace(/[/\\?%*:|"<>]/g, "").replace(/\s+/g, "_");
    return `${safeName || `monster_Usa_${fallbackId}`}.${extension}`;
  }

  function buildHtmlFromProfile(profile) {
    try {
      const name =
        profile?.name ||
        `${profile?.personal_details?.[0]?.first_name || ""} ${
          profile?.personal_details?.[0]?.last_name || ""
        }`.trim();
      const email = profile?.email?.[0]?.id || "";
      const phone = profile?.mobile_details?.[0]?.number
        ? `+${profile?.mobile_details?.[0]?.country_code || ""} ${
            profile?.mobile_details?.[0]?.number
          }`.trim()
        : "";
      const location =
        (Array.isArray(profile?.current_location)
          ? profile?.current_location?.[0]?.text
          : profile?.current_location?.text) || "";
      const summary = Array.isArray(profile?.title)
        ? profile?.title?.[0]?.value
        : profile?.title?.value || "";
      const resumeText = profile?.resume_text || "";
      const skills = (
        Array.isArray(profile?.skills)
          ? profile.skills.map((s) => s?.text || s?.value || s).filter(Boolean)
          : []
      ).join(", ");
      const edu = Array.isArray(profile?.educations)
        ? profile.educations
            .map(
              (e) =>
                `${e.degree || ""} - ${e.college || ""} (${
                  e.passing_year || "N/A"
                })`,
            )
            .join("<br/>")
        : "";
      const currEmp =
        Array.isArray(profile?.current_employment) &&
        profile.current_employment[0]
          ? profile.current_employment[0]
          : null;
      const empBlock = currEmp
        ? `${
            (currEmp.employer && (currEmp.employer.text || currEmp.employer)) ||
            ""
          } - ${
            (currEmp.designation &&
              (currEmp.designation.text || currEmp.designation)) ||
            ""
          }<br/>${currEmp.description || ""}`
        : "";

      const safe = (s) => (typeof s === "string" ? s : "");
      const body = `
        <div style="font-family: Arial, sans-serif; font-size: 12pt; line-height: 1.4;">
          <h1 style="margin: 0 0 8px 0; font-size: 18pt;">${safe(name)}</h1>
          <div style="margin-bottom: 10px; color: #555;">
            ${safe(email)}${email && phone ? " | " : ""}${safe(phone)}${
              (email || phone) && location ? " | " : ""
            }${safe(location)}
          </div>
          ${
            summary
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Summary</h2><div>${safe(
                  summary,
                )}</div>`
              : ""
          }
          ${
            skills
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Skills</h2><div>${safe(
                  skills,
                )}</div>`
              : ""
          }
          ${
            empBlock
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Experience</h2><div>${empBlock}</div>`
              : ""
          }
          ${
            edu
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Education</h2><div>${edu}</div>`
              : ""
          }
          ${
            resumeText
              ? `<h2 style="font-size:14pt; margin:16px 0 6px;">Resume</h2><pre style="white-space: pre-wrap; font-family: inherit;">${safe(
                  resumeText,
                )}</pre>`
              : ""
          }
        </div>
      `;
      return body;
    } catch {
      return "";
    }
  }

  const makeMonsterApiCall = async (apiCallFunction) => {
    try {
      const token = localStorage.getItem("monsterTokenUsa");
      return await apiCallFunction(token);
    } catch (error) {
      if (isTokenInvalidError(error)) {
        try {
          localStorage.removeItem("monsterTokenUsa");
          const newToken = await fetchMonsterToken();
          return await apiCallFunction(newToken);
        } catch (refreshError) {
          throw refreshError;
        }
      } else {
        throw error;
      }
    }
  };

  useEffect(() => {
    const initializeMonsterToken = async () => {
      const existingToken = localStorage.getItem("monsterTokenUsa");
      const expiryTime = localStorage.getItem("monsterTokenUsa");
      const isExpired = expiryTime ? Date.now() >= parseInt(expiryTime) : true;
      if (!existingToken || isExpired) {
        try {
          await fetchMonsterToken();
        } catch (error) {
          console.error("Failed to initialize Monster token:", error);
        }
      }
    };
    initializeMonsterToken();
  }, []);

  const validationSchema = Yup.object({
    jobTitles: Yup.string().max(200, "Max 200 characters"),
    yearsExpFrom: Yup.number().min(0).max(50).nullable(),
    yearsExpTo: Yup.number()
      .min(Yup.ref("yearsExpFrom"), "Must be ≥ From")
      .max(50)
      .nullable(),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadMoreMonsterResults = async () => {
    if (isLoadingMore || isLoading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    await handleSearch(nextPage);
  };

  const lastMonsterCardRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreMonsterResults();
          }
        },
        { root: null, rootMargin: "100px", threshold: 0.1 },
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, loadMoreMonsterResults],
  );

  const makeSearchCall = async (
    tokenOverride = null,
    values = formik.values,
    pageNum = 1,
    pageSize = 10,
  ) => {
    const token = tokenOverride || localStorage.getItem("monsterTokenUsa");
    if (!token)
      throw new Error("No Monster token found. Please authenticate first.");

    const locations = [];
    if (values.city || values.state || values.postalcode) {
      const locationObj = {
        country: "US",
      };

      if (values.city) locationObj.city = values.city;
      if (values.state) locationObj.state = values.state;
      if (values.postalcode) locationObj.postalCode = values.postalcode;

      if (values.distance) {
        locationObj.radius = parseInt(values.distance);
        locationObj.radiusUnit = values.distanceUnit || "Miles";
      }

      locations.push(locationObj);
    }

    let skills = [];
    if (values.skills) {
      const importance = values.skillMode === "any" ? "NiceToHave" : "Required";

      skills = values.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((skill) => ({
          name: skill,
          importance: importance,
        }));
    }

    let degrees = [];
    if (values.degree) {
      degrees = values.degree
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean)
        .map((d) => ({
          degreeName: d,
          importance: "Required",
        }));
    }

    let companies = [];
    if (values.company?.length) {
      companies = values.company;
    }

    let yearsOfExperience;
    if (values.yearsExpFrom || values.yearsExpTo) {
      let expression = "";

      if (values.yearsExpFrom && values.yearsExpTo) {
        expression = `${values.yearsExpFrom}-${values.yearsExpTo}`;
      } else if (values.yearsExpFrom) {
        expression = `${values.yearsExpFrom}+`;
      } else if (values.yearsExpTo) {
        expression = `<${values.yearsExpTo}`;
      }

      yearsOfExperience = {
        expression,
        importance: "Required",
      };
    }

    const payload = {
      country: "US",
      searchType: "semantic",
      semantic: {
        jobTitles: values.jobTitles
          ? values.jobTitles.split(",").map((j) => j.trim())
          : [],
        skills,
        locations,
        degrees,
        companies,
        ...(yearsOfExperience && { yearsOfExperience }),
      },
    };

    if (values.lastActiveDays) {
      const minutes = parseInt(values.lastActiveDays) * 24 * 60;
      payload.semantic.lastActiveMaximumAge = minutes;
    }

    if (values.resumeUpdateMaxDay) {
      const minutes = parseInt(values.resumeUpdateMaxDay) * 24 * 60;
      payload.semantic.resumeUpdatedMaximumAge = minutes;
    }

    if (values.candidateName) {
      payload.semantic.candidateName = values.candidateName;
    }

    const params = new URLSearchParams({
      monsterus_access_token: token,
      perPage: String(pageSize),
      page: String(pageNum),
    });

    return await post(
      MONSTER_SEARCH_BASE_URL,
      `${MONSTER_SEARCH_USA}?${params.toString()}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    );
  };

  const handleSearch = async (pageNum = 1) => {
    if (pageNum === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const res = await makeMonsterApiCall((token) =>
        makeSearchCall(token, formik.values, pageNum),
      );
      const parsedResponse = typeof res === "string" ? JSON.parse(res) : res;

      const newPage = parsedResponse?.candidates || [];
      const total = parsedResponse?.boards?.[0]?.matched || 0;

      setResults((prev) =>
        pageNum === 1 ? newPage : [...(prev || []), ...newPage],
      );
      setTotalCount(total);

      setHasMore(
        newPage.length > 0 && (results || []).length + newPage.length < total,
      );
    } catch (err) {
      console.error("Monster search failed", err);
      Toast("Search failed. Please try again.");
    } finally {
      if (pageNum === 1) {
        setIsLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const setMapping = (monsterPuuid, candidateId) => {
    const map = { ...parsedMap, [monsterPuuid]: candidateId };
    setParsedMap(map);
    localStorage.setItem("monsterCandidateIdMap", JSON.stringify(map));
  };

  const retryFetchCandidate = async (
    candidateId,
    attempts = 5,
    delayMs = 5000,
    signal = null,
  ) => {
    for (let i = 0; i < attempts; i++) {
      if (signal?.aborted) return null;
      const full = await fetchCandidateById(candidateId);
      if (full && full.id) return full;
      if (signal?.aborted) return null;
      await new Promise((r) => setTimeout(r, delayMs));
    }
    return null;
  };

  const fetchCandidateById = async (id) => {
    try {
      const res = await get(VYLABS_BASE_URL, `${GET_CANDIDATE_BY_ID}?id=${id}`);
      const candidate = res?.candidate;
      const candidatefiles = res?.files;
      const parsedResumeData = candidate?.parsed_resume
        ? JSON.parse(candidate.parsed_resume)
        : null;
      const presignedUrl =
        candidatefiles && candidatefiles.length > 0
          ? candidatefiles[0].presigned_url
          : null;
      const updatedCandidate = {
        ...candidate,
        years_of_experience:
          candidate?.years_of_experience ?? candidate?.yearsOfExperience ?? 0,
        location:
          candidate?.location ||
          [candidate?.city, candidate?.state, candidate?.country]
            .filter(Boolean)
            .join(", ") ||
          "",
        city: candidate?.city || "",
        state: candidate?.state || "",
        country: candidate?.country || "",
        secondaryEmail: candidate?.secondary_email,
        secondaryPhone: candidate?.secondary_phone,
        lineAddress1: candidate.line_address_1,
        lineAddress2: candidate.line_address_2,
        zipCode: candidate.zip_code,
        preferredSalary: candidate.preferred_salary,
        vendorID: candidate?.vendor_id,
        parsedResumeData,
        files: candidatefiles || null,
        presignedUrl,
        isFileBased: false,
        avatar: res?.profile_img_url,
      };
      if (updatedCandidate.vendorID) {
        try {
          const vendorResponse = await get(
            VYLABS_BASE_URL,
            `${GET_VENDOR_DETAILS_BY_ID}?id=${updatedCandidate.vendorID}`,
          );
          setAssignedVendor(vendorResponse);
          updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
        } catch {
          updatedCandidate.vendorDetails = null;
          setAssignedVendor(null);
        }
      } else {
        updatedCandidate.vendorDetails = null;
        setAssignedVendor(null);
      }
      return updatedCandidate;
    } catch (e) {
      console.error("fetchCandidateById error", e);
      return null;
    }
  };

  const fetchMonsterProfile = async (textResumeID, signal) => {
    const makeViewCall = async (tokenOverride = null) => {
      const token = tokenOverride || localStorage.getItem("monsterTokenUsa");
      if (!token)
        throw new Error("No Monster token found. Please authenticate first.");

      return await get(
        MONSTER_SEARCH_BASE_URL,
        `${MONSTER_VIEW_USA}/?monsterus_access_token=${token}&can_id=${textResumeID}`,
      );
    };
    const res = await makeMonsterApiCall(makeViewCall);
    let parsed = typeof res === "string" ? JSON.parse(res) : res;
    const profile = parsed?.data?.searchResult?.[0] || parsed.data || parsed;
    return profile;
  };

  const handlePopupClose = () => {
    setHasCandidateId(false);
    setRealCandidateId();
    setIsImportFailed(false);
    if (abortController) abortController.abort("Popup closed by user");
    try {
      if (wsRef) wsRef.close();
    } catch (e) {}
    if (wsPingInterval) clearInterval(wsPingInterval);
    if (wsPingTimeout) clearTimeout(wsPingTimeout);
    if (localCandidate?.files?.[0]?.localUrl) {
      URL.revokeObjectURL(localCandidate.files[0].localUrl);
    }
    setIsPopupOpen(false);
    setIsProcessingView(false);
    setLocalCandidate(null);
    setFallbackMonsterPopupOpen(false);
    setSelectedMonsterId(null);
    setAbortController(null);
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
  };
  const handleVendorInputChange = (_, value) => {
    if (value && value.length >= 3) {
      fetchVendors(value);
    } else {
      setVendorList([]);
    }
  };

  const handleViewCandidate = async (monsterId) => {
    if (isProcessingView || !monsterId) return;

    setSelectedMonsterId(monsterId);
    const controller = new AbortController();
    setAbortController(controller);
    setIsPopupOpen(true);
    setIsProcessingView(true);
    setLocalCandidate(null);
    const appendToMonsterCandidateId =
      "monster_Usa_" + String(monsterId).trim();
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const notesRes = await get(
          VYLABS_BASE_URL,
          `${GET_CANDIDATES_BY_NOTES}?notes=${appendToMonsterCandidateId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        const data = notesRes;
        if(notesRes?.candidate?.id) {
          setHasCandidateId(true);
          setRealCandidateId(notesRes.candidate.id);
        }
        const mappedId =
          data?.id ||
          data?.candidate?.id ||
          (Array.isArray(data) && data[0]?.id) ||
          null;

        if (mappedId) {
          const full = await fetchCandidateById(mappedId);
          if (full && !controller.signal.aborted) {
            setLocalCandidate(full);
            setIsProcessingView(false);
            return;
          }
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED") return;
        console.warn("Error checking for existing candidate by notes:", err);
      }
    }

    try {
      const profile = await fetchMonsterProfile(monsterId, controller.signal);
      setLastProfileDetails(profile);

      const candidateRawName = (
        profile?.identity?.name ||
        profile?.name ||
        `${profile?.personal_details?.[0]?.first_name || ""} ${
          profile?.personal_details?.[0]?.last_name || ""
        }`.trim() ||
        ""
      ).trim();
      const htmlFileName = getCandidateFileName(
        candidateRawName,
        monsterId,
        "html",
      );

      const resumeHtml = profile?.resume || buildHtmlFromProfile(profile);
      const resumeFile = resumeHtml
        ? htmlToFile(resumeHtml, htmlFileName)
        : null;

      const transformedCandidate = transformMonsterProfileToCandidate(
        profile,
        monsterId,
      );

      if (transformedCandidate && !controller.signal.aborted) {
        const displayCandidate = {
          ...transformedCandidate.candidate,
          isTemp: true,
          vendorID: envConfig.monsterUsaVendorID,
          vendorDetails: null,
          files: resumeFile ? [resumeFile] : transformedCandidate.files,
          presignedUrl: transformedCandidate.files[0]?.presigned_url || null,
          isFileBased: false,
          avatar: transformedCandidate.profile_img_url,
          parsedResumeData: transformedCandidate.candidate.parsedResumeData,
          isTransformed: true,
          source: "Monster US",
          monsterId: monsterId,
          isLoadingResume: true,
        };

        setLocalCandidate(displayCandidate);
        setIsProcessingView(false);
        setFallbackMonsterPopupOpen(false);

        startBackgroundResumeParsing(
          monsterId,
          profile,
          controller.signal,
          resumeFile,
        );
        return;
      }

      setIsProcessingView(false);
      setFallbackMonsterPopupOpen(true);
    } catch (e) {
      if (controller.signal.aborted) return;
      console.error("Error viewing Monster candidate:", e);

      if (lastProfileDetails) {
        const transformedCandidate = transformMonsterProfileToCandidate(
          lastProfileDetails,
          monsterId,
        );
        if (transformedCandidate) {
          const candidateRawName = (
            lastProfileDetails?.identity?.name ||
            lastProfileDetails?.name ||
            `${lastProfileDetails?.personal_details?.[0]?.first_name || ""} ${
              lastProfileDetails?.personal_details?.[0]?.last_name || ""
            }`.trim() ||
            ""
          ).trim();
          const htmlFileName = getCandidateFileName(
            candidateRawName,
            monsterId,
            "html",
          );
          const resumeHtml =
            lastProfileDetails?.resume ||
            buildHtmlFromProfile(lastProfileDetails);
          const resumeFile = resumeHtml
            ? htmlToFile(resumeHtml, htmlFileName)
            : null;

          const displayCandidate = {
            ...transformedCandidate.candidate,
            isTemp: true,
            vendorID: envConfig.monsterUsaVendorID,
            vendorDetails: null,
            files: resumeFile ? [resumeFile] : transformedCandidate.files,
            presignedUrl: transformedCandidate.files[0]?.presigned_url || null,
            isFileBased: false,
            avatar: transformedCandidate.profile_img_url,
            parsedResumeData: transformedCandidate.candidate.parsedResumeData,
            isTransformed: true,
            source: "Monster US",
            monsterId: monsterId,
            isLoadingResume: true,
          };
          setLocalCandidate(displayCandidate);
          setIsProcessingView(false);
          setFallbackMonsterPopupOpen(false);
          startBackgroundResumeParsing(
            monsterId,
            lastProfileDetails,
            controller.signal,
            resumeFile,
          );
          return;
        }
      }

      setIsProcessingView(false);
      setFallbackMonsterPopupOpen(true);
    }
  };

  const startBackgroundResumeParsing = async (
    monsterId,
    profile,
    signal,
    resumeFileParam,
  ) => {
    try {
      let resumeFile = resumeFileParam;
      if (!resumeFile && profile) {
        const resumeHtml = profile?.resume || buildHtmlFromProfile(profile);
        if (resumeHtml) {
          const candidateRawName = (
            profile?.identity?.name ||
            profile?.name ||
            `${profile?.personal_details?.[0]?.first_name || ""} ${
              profile?.personal_details?.[0]?.last_name || ""
            }`.trim() ||
            ""
          ).trim();
          const htmlFileName = getCandidateFileName(
            candidateRawName,
            monsterId,
            "html",
          );
          resumeFile = htmlToFile(resumeHtml, htmlFileName);
        }
      }
      if (!resumeFile) {
        if (
          !profile?.identity?.name &&
          !profile?.name &&
          !profile?.personal_details?.[0]?.first_name &&
          !profile?.personal_details?.[0]?.last_name
        ) {
          setLocalCandidate((prev) =>
            prev ? { ...prev, isLoadingResume: false } : null,
          );
          setIsImportFailed(true);
          return;
        }

        const hasEmail =
          profile?.identity?.emailAddress ||
          profile?.contact?.emailAddress ||
          profile?.email?.[0]?.id;

        const hasPhone =
          profile?.phoneNumbers?.[0]?.phoneNumberValue ||
          profile?.mobile_details?.[0]?.number;

        if (!hasEmail && !hasPhone) {
          setLocalCandidate((prev) =>
            prev ? { ...prev, isLoadingResume: false } : null,
          );
          setIsImportFailed(true);
          return;
        }

        setLocalCandidate((prev) =>
          prev ? { ...prev, isLoadingResume: false } : null,
        );
        return;
      }
      const parseResult = await parseMonsterResumeHeadless(resumeFile, {
        signal,
      });

      if (signal.aborted) return;
      if (parseResult?.candidateId) {
        processStatusRef.current = "SUCCESS";
      } else {
        processStatusRef.current = "FAILED";
      }

      if (parseResult?.candidateId) {
        setMapping(monsterId, parseResult.candidateId);

        if (processStatusRef.current === "SUCCESS") {
          const appendToCandidateId = "monster_Usa_" + String(monsterId).trim();

          const p = typeof profile === "string" ? JSON.parse(profile) : profile;

          const name =
            p?.identity?.name ||
            p?.name ||
            `${p?.personal_details?.[0]?.first_name || ""} ${
              p?.personal_details?.[0]?.last_name || ""
            }`.trim() ||
            "Unknown Name";

          const email =
            p?.identity?.emailAddress ||
            p?.contact?.emailAddress ||
            p?.email?.[0]?.id ||
            "";

          const phone =
            p?.phoneNumbers?.[0]?.phoneNumberValue ||
            p?.mobile_details?.[0]?.number ||
            "";

          const formatUSPhone = (phone) => {
            if (!phone) return null;
            const cleaned = phone.replace(/\D/g, "");
            if (cleaned.length === 10) return `+1${cleaned}`;
            if (cleaned.length === 11 && cleaned.startsWith("1"))
              return `+${cleaned}`;
            return null;
          };

          const city =
            p?.location?.city || p?.contact?.address?.[0]?.city || "";
          const state =
            p?.location?.state || p?.contact?.address?.[0]?.state || "";
          const country =
            p?.location?.country || p?.contact?.address?.[0]?.country || "";

          const yearsExp = p?.yearsOfExperience || 0;
          const designation = p?.relevance?.experience?.title?.name || "";

          const payload = {
            id: parseResult.candidateId,
            vendorID: envConfig.monsterUsaVendorID,
            notes: appendToCandidateId,
            name,
            email,
            phone: formatUSPhone(phone),
            city,
            state,
            country,
            designation,
            yearsOfExperience: yearsExp,
            source: "Monster US",
          };

          Object.keys(payload).forEach(
            (key) => !payload[key] && delete payload[key],
          );

          post(VYLABS_BASE_URL, UPDATE_CANDIDATE_DETAILS, payload)
            .then(() => {})
            .catch((err) => {
              console.error(
                `[Background Update] Notes update failed for candidate: ${parseResult.candidateId}`,
                err,
              );
            });
        } else {
          console.warn(
            "[UPDATE] Skipped UPDATE_CANDIDATE_DETAILS because process-status is not SUCCESS",
          );
        }

        const full = await retryFetchCandidate(
          parseResult.candidateId,
          5,
          5000,
          signal,
        );
        if (full && !signal.aborted) {
          setLocalCandidate({ ...full, isLoadingResume: false });
        }
        if(parseResult.candidateId)
        {
          setHasCandidateId(true);
          setRealCandidateId(parseResult.candidateId);
        }
      } else {
        processStatusRef.current = "FAILED";
        let newCandidateId = null;

        if (parseResult?.status === "INCOMPLETE") {
          const p =
            typeof profile === "string" ? JSON.parse(profile) : profile;

          const name =
            p?.identity?.name ||
            p?.name ||
            `${p?.personal_details?.[0]?.first_name || ""} ${
              p?.personal_details?.[0]?.last_name || ""
            }`.trim();

          const email =
            p?.identity?.emailAddress ||
            p?.contact?.emailAddress ||
            p?.email?.[0]?.id ||
            "";

          const phone =
            p?.phoneNumbers?.[0]?.phoneNumberValue ||
            p?.mobile_details?.[0]?.number ||
            "";

          if (!name || (!email && !phone)) {
            setLocalCandidate((prev) =>
              prev ? { ...prev, isLoadingResume: false } : null,
            );
            setIsImportFailed(true);
            return;
          }

          newCandidateId = await createCandidateFromFallback(
            monsterId,
            parseResult.fileContent,
            profile,
            parseResult.fileID,
          );
        } else if (parseResult?.status === "FAILED") {
          const p =
            typeof profile === "string" ? JSON.parse(profile) : profile;

          const name =
            p?.identity?.name ||
            p?.name ||
            `${p?.personal_details?.[0]?.first_name || ""} ${
              p?.personal_details?.[0]?.last_name || ""
            }`.trim();

          const email =
            p?.identity?.emailAddress ||
            p?.contact?.emailAddress ||
            p?.email?.[0]?.id ||
            "";

          const phone =
            p?.phoneNumbers?.[0]?.phoneNumberValue ||
            p?.mobile_details?.[0]?.number ||
            "";

          if (!name || (!email && !phone)) {
            setLocalCandidate((prev) =>
              prev ? { ...prev, isLoadingResume: false } : null,
            );
            setIsImportFailed(true);
            return;
          }

          newCandidateId = await createCandidateFromFallback(
            monsterId,
            parseResult.fileContent,
            profile,
            null,
          );

          if (newCandidateId && !signal.aborted) {
            await post(VYLABS_BASE_URL, LINK_CANDIDATE_RESUME, {
              candidate_id: newCandidateId,
              file_id: parseResult.fileID,
            });
          }
        } else {
          const p =
            typeof profile === "string" ? JSON.parse(profile) : profile;

          const name =
            p?.identity?.name ||
            p?.name ||
            `${p?.personal_details?.[0]?.first_name || ""} ${
              p?.personal_details?.[0]?.last_name || ""
            }`.trim();

          const email =
            p?.identity?.emailAddress ||
            p?.contact?.emailAddress ||
            p?.email?.[0]?.id ||
            "";

          const phone =
            p?.phoneNumbers?.[0]?.phoneNumberValue ||
            p?.mobile_details?.[0]?.number ||
            "";

          if (!name || (!email && !phone)) {
            setLocalCandidate((prev) =>
              prev ? { ...prev, isLoadingResume: false } : null,
            );
            setIsImportFailed(true);
            return;
          }

          newCandidateId = await createCandidateFromFallback(
            monsterId,
            parseResult.fileContent,
            profile,
            parseResult.fileID,
          );
        }

        if (newCandidateId && !signal.aborted) {
          setMapping(monsterId, newCandidateId);

          const full = await retryFetchCandidate(
            newCandidateId,
            5,
            5000,
            signal,
          );
          if (full && !signal.aborted) {
            setLocalCandidate({ ...full, isLoadingResume: false });
          }
          setHasCandidateId(true);
          setRealCandidateId(newCandidateId);
        } else {
          setLocalCandidate((prev) =>
            prev ? { ...prev, isLoadingResume: false } : null,
          );
        }
      }
    } catch (err) {
      if (signal.aborted) return;
      console.error("[Background Parsing] Error:", err);
      setLocalCandidate((prev) =>
        prev ? { ...prev, isLoadingResume: false } : null,
      );
    }
  };
  const transformMonsterProfileToCandidate = (profile, monsterId) => {
    try {
      const p = typeof profile === "string" ? JSON.parse(profile) : profile;
      const name = p?.identity?.name || "Unknown Name";
      const email = p?.identity?.emailAddress || p?.contact?.emailAddress || "";
      const phone = p?.phoneNumbers?.[0]?.phoneNumberValue || "";
      const city = p?.location?.city || p?.contact?.address?.[0]?.city || "";
      const state = p?.location?.state || p?.contact?.address?.[0]?.state || "";
      const country =
        p?.location?.country || p?.contact?.address?.[0]?.country || "";
      const location =
        (p?.location
          ? [p.location.city, p.location.state, p.location.country]
              .filter((v) => v)
              .join(", ")
          : null) ||
        p?.contact?.address?.[0]?.city ||
        "";
      const skills = Array.isArray(p?.relevance?.skills)
        ? p.relevance.skills.map((s) => s.name).filter(Boolean)
        : [];
      const yearsExp = p?.yearsOfExperience || 0;
      const designation = p?.relevance?.experience?.title?.name || "";
      const employer = p?.relevance?.experience?.company?.name || "";

      const parsedResume = {
        BasicInfo: {
          Name: name,
          Email: email,
          Phone: phone,
          CurrentLocationCity: location,
          YearsOfExperience: parseFloat(yearsExp) || 0,
        },
        Skills: skills,
        EducationHistory: [],
        CareerHistory: { JobDetails: [] },
        ProfileSummary: "",
      };

      const candidate = {
        id: `temp_monster_usa_${monsterId}`,
        name,
        email,
        phone,
        city: city,
        state: state,
        country: country,
        location: location,
        parsed_resume: JSON.stringify(parsedResume),
        skills,
        status: "New Profile",
        notes: `monster_Usa_${monsterId}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        vendorID: envConfig.monsterUsaVendorID,
        designation,
        employer,
        experience: parseFloat(yearsExp) || 0,
        parsedResumeData: parsedResume,
      };
      const files = [];

      return {
        candidate,
        files,
        profile_img_url: null,
      };
    } catch (error) {
      console.error("Error transforming Monster USA profile:", error);
      return null;
    }
  };

  const handleEditCandidate = () => {
    if (localCandidate?.isTemp) {
      Toast("error", "Please wait until candidate is saved.");
      return;
    }
    if (!localCandidate) return;

    const getValidExperience = (value) => {
      const num = Number(value);
      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };
    const experienceValue = getValidExperience(
      localCandidate?.years_of_experience ??
        localCandidate?.yearsOfExperience ??
        localCandidate?.experience,
    );

    const formattedCandidate = {
      ...localCandidate,
      name: localCandidate.name || "",
      email: localCandidate.email || "",
      phone: localCandidate.phone || "",
      skills: Array.isArray(localCandidate.skills)
        ? localCandidate.skills
        : localCandidate.parsedResumeData?.Skills || [],
      designation: localCandidate.designation || "",
      experience: experienceValue,
      city: localCandidate.city || "",
      state: localCandidate.state || "",
      country: localCandidate.country || "",
      vendorID: localCandidate.vendorID || "",
    };

    setOriginalCandidateData({ ...localCandidate });
    setEditedCandidateData(formattedCandidate);
    setIsEditingCandidate(true);
  };

  const handleCandidateChange = (data) => {
    setEditedCandidateData((prev) => ({ ...prev, ...data }));
  };

  const saveCandidateChanges = async () => {
    if (!editedCandidateData) {
      Toast("error", "No changes to save");
      return;
    }

    try {
      const candidateData = { id: localCandidate.id };

      Object.keys(editedCandidateData).forEach((key) => {
        if (editedCandidateData[key] !== localCandidate[key]) {
          candidateData[key] = editedCandidateData[key];
        }
      });

      if (Object.keys(candidateData).length === 1) {
        Toast("info", "No changes were made.");
        setIsEditingCandidate(false);
        return;
      }

      Object.keys(candidateData).forEach((key) => {
        if (key === "preferredSalary") {
          candidateData[key] = Number(candidateData[key]);
        }
      });

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }

      if (candidateData.skills && Array.isArray(candidateData.skills)) {
        candidateData.skills = [
          ...new Set(
            candidateData.skills
              .map((s) => String(s || "").trim())
              .filter(Boolean)
              .map((s) => s.toLowerCase().replace(/\s+/g, " ")),
          ),
        ];
      }
      const originalVendor = originalCandidateData?.vendorID ?? null;
      const editedVendor = editedCandidateData?.vendorID ?? null;

      if (originalVendor !== editedVendor) {
        candidateData.vendorID = editedVendor;
      } else {
        delete candidateData.vendorID;
      }
      delete candidateData.vendor_id;

      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_CANDIDATE_DETAILS,
        candidateData,
      );

      if (response) {
        const updated = { ...localCandidate, ...candidateData };
        setLocalCandidate(updated);
        setIsEditingCandidate(false);
        setOriginalCandidateData(null);
        setEditedCandidateData(null);
        Toast("success", "Candidate updated successfully");
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      Toast("error", "Failed to update candidate");
    }
  };

  const resetCandidateEdit = () => {
    if (originalCandidateData) {
      setLocalCandidate(originalCandidateData);
    }
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
  };

  const formik = useFormik({
    initialValues: {
      jobTitles: "",
      skills: "",
      skillMode: "any",
      city: "",
      state: "",
      postalcode: "",
      noticePeriod: "",
      yearsExpFrom: "",
      yearsExpTo: "",
      degree: "",
      distance: "",
      distanceUnit: "",
      candidateName: "",
      company: [],
      gender: "",
      lastActiveDays: "",
      resumeUpdateMaxDay: "",
      email: false,
      phone: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      const hasJobTitles = values.jobTitles && values.jobTitles.trim() !== "";

      if (!hasJobTitles && !values.skills?.length) {
        Toast("error", "Please provide a job title or at least one skill.");
        return;
      }
      setIsLoading(true);
      setPage(1);
      setResults([]);
      setHasSearched(true);
      setSearchQuery(values.jobTitles);
      await handleSearch(1);
      setIsLoading(false);
    },
  });

  return (
    <div className="monster-usa-page">
      <div className="header-main-container">
        <img
          alt=""
          src={isDarkMode ? CandidateDarkIcon : CandidateLightIcon}
          className="header-title-icon"
        />
        <h2 className="monster-search-title">Monster Search ( USA )</h2>
        <Button
          label="Back to Vendor"
          style={{ padding: "5px 10px", marginLeft: "auto" }}
          event={() => navigate("/vendors")}
          iconSrc={backArrowIcon}
        />
      </div>
      <div className="advanced-search-toggle">
        <h4 onClick={toggleAdvancedSearch} className="advance-search-text">
          {showAdvancedSearch ? "Hide Advanced Search" : " Advanced Search"}
        </h4>
        <CustomTooltip
          title={
            showAdvancedSearch ? "Hide advanced fields" : "Show advanced fields"
          }
          arrow
        >
          <img
            alt="infoIcon"
            src={infoIcon}
            style={{ cursor: "pointer", width: "16px", height: "16px" }}
          />
        </CustomTooltip>
      </div>
      <div className="monster-usa-search-container">
        <form onSubmit={formik.handleSubmit}>
          <div
            className="default-search-usa-fields"
            style={{ gap: "20px", padding: "20px" }}
          >
            <TextInput
              label="Job Title"
              name="jobTitles"
              placeholder="e.g. Java Developer"
              value={formik.values.jobTitles}
              onChange={formik.handleChange}
              error={formik.touched.jobTitles && formik.errors.jobTitles}
              required={true}
            />

            <div className="field-group">
              <div
                className="skills-or-and-field"
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}
              >
                <label>
                  Skills <span style={{ color: "red" }}>*</span>
                </label>
                <div className="mode-usa-toggle">
                  <label>
                    <input
                      type="radio"
                      name="skillMode"
                      value="any"
                      checked={formik.values.skillMode === "any"}
                      onChange={formik.handleChange}
                    />
                    Any (OR)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="skillMode"
                      value="all"
                      checked={formik.values.skillMode === "all"}
                      onChange={formik.handleChange}
                    />
                    All (AND)
                  </label>
                </div>
              </div>

              <div className="form-group">
                <TextInput
                  name="skills"
                  placeholder="e.g. JavaScript, React, Node.js"
                  value={formik.values.skills}
                  onChange={formik.handleChange}
                  error={formik.touched.skills && formik.errors.skills}
                />
              </div>
            </div>
          </div>

          {showAdvancedSearch && (
            <div
              className="advance-search-fields"
              style={{ gap: "20px", padding: "20px", marginTop: "-20px" }}
            >
              <>
                <TextInput
                  label="Degree"
                  placeholder="Enter your degree"
                  name="degree"
                  value={formik.values.degree}
                  onChange={formik.handleChange}
                  style={{ marginTop: "5px" }}
                />

                <div className="field-group" style={{ marginTop: "20px" }}>
                  <label style={{ marginTop: "-15px" }}>
                    Years of Experience
                  </label>
                  <div className="exp-range">
                    <TextInput
                      name="yearsExpFrom"
                      placeholder="From ( Min )"
                      value={formik.values.yearsExpFrom}
                      onChange={formik.handleChange}
                    />
                    <TextInput
                      name="yearsExpTo"
                      placeholder="To ( Max )"
                      value={formik.values.yearsExpTo}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>

                <TextInput
                  label="Candidate Name"
                  placeholder="Enter Candidate Name"
                  name="candidateName"
                  value={formik.values.candidateName}
                  onChange={formik.handleChange}
                />

                <DynamicMultipleSelectCheckmarks
                  options={DiceCompany}
                  label="Company"
                  placeholder="Select company(s)"
                  value={formik.values.company}
                  onChange={(values) => formik.setFieldValue("company", values)}
                />

                <TextInput
                  label="Distance"
                  placeholder="Enter your search distance"
                  name="distance"
                  value={formik.values.distance}
                  onChange={formik.handleChange}
                  style={{ marginTop: "5px" }}
                />

                <SelectInput
                  label="Distance Unit"
                  options={DistanceUnit}
                  name="distanceUnit"
                  value={formik.values.distanceUnit}
                  onChange={formik.handleChange}
                  wrapperStyle={{
                    display: "flex",
                    width: "100%",
                    marginBottom: "15px",
                  }}
                  labelStyle={{
                    fontSize: "16px",
                    fontWeight: 600,
                    width: "fit-content",
                    marginBottom: "12px",
                  }}
                />

                <div
                  className="city-state-usa"
                  style={{
                    gap: "10px",
                    marginTop: "-6px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                  }}
                >
                  <TextInput
                    name="city"
                    label="City"
                    placeholder="e.g. Los Angeles,Chicago "
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    error={formik.touched.city && formik.errors.city}
                    style={{ marginTop: "5px" }}
                  />

                  <TextInput
                    name="state"
                    label="State"
                    placeholder="e.g. California,New York "
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    error={formik.touched.state && formik.errors.state}
                    style={{ marginTop: "5px" }}
                  />
                </div>

                <TextInput
                  name="postalcode"
                  label="Postal Code"
                  placeholder="e.g.  90210 "
                  value={formik.values.postalcode}
                  onChange={formik.handleChange}
                  error={formik.touched.postalcode && formik.errors.postalcode}
                />

                <TextInput
                  name="lastActiveDays"
                  label="Last Active Days"
                  placeholder="e.g. 30"
                  value={formik.values.lastActiveDays}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.lastActiveDays &&
                    formik.errors.lastActiveDays
                  }
                />

                <TextInput
                  name="resumeUpdateMaxDay"
                  label="Resume Last update Days"
                  placeholder="e.g. 30"
                  value={formik.values.resumeUpdateMaxDay}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.resumeUpdateMaxDay &&
                    formik.errors.resumeUpdateMaxDay
                  }
                />
              </>
            </div>
          )}
        </form>
      </div>

      <div className="full-width">
        <Button
          label={isLoading ? "Searching...." : "Search Candidate"}
          event={formik.handleSubmit}
          disabled={isLoading}
        />
      </div>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <VybogTALLoaderAnimation />
        </div>
      ) : (
        results && (
          <div className="monster-results-section">
            {results.length > 0 && (
              <div className="monster-usa-results-toolbar">
                <span className="monster-usa-results-count">
                  <strong>Total candidates:</strong> {totalCount}
                </span>
                <div className="monster-usa-card-toggle-group" role="group" aria-label="Candidate card view">
                  {[
                    { value: "compact", label: "Compact Card", icon: compactIcon },
                    { value: "detailed", label: "Detailed Card", icon: detailedIcon },
                  ].map(({ value, label, icon }) => (
                    <Button
                      key={value}
                      type="button"
                      label={label}
                      iconSrc={icon}
                      iconPosition="left"
                      iconWidth="14px"
                      hoverd={false}
                      className={`monster-usa-toggle-btn ${cardView === value ? "active" : ""}`}
                      aria-pressed={cardView === value}
                      event={() => setCardView(value)}
                      backgroundColor={cardView === value ? "#6860ee" : "transparent"}
                      color={cardView === value ? "#ffffff" : "var(--grid-na-text, #64748b)"}
                      style={{ padding: "6px 14px", borderRadius: "6px", fontSize: "13px", gap: "8px" }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className={`monster-candidates-grid ${cardView === "detailed" ? "detailed-view" : ""}`}>
              {results.map((candidate, index) => {
                const isLast = index === results.length - 1;
                const cardData = mapMonsterCandidateToCardProps(candidate);
                const isConfidential =
                  candidate?.identity?.confidential === true;

                return (
                  <div
                    key={candidate.identity.textResumeID}
                    ref={isLast ? lastMonsterCardRef : null}
                  >
                    <MonsterUsaCard
                      {...cardData}
                      cardView={cardView}
                      viewEvent={
                        isConfidential
                          ? null
                          : () =>
                              handleViewCandidate(
                                candidate?.identity?.textResumeID,
                              )
                      }
                      showView={true}
                      cardbackgroundColor="var(--card__background)"
                      cardColor="var(--header__color)"
                      skillbackgroundColor="var(--skill-badge-bg)"
                      skillColor="var(--skill-badge-color)"
                      viewDisabled={isConfidential}
                    />
                  </div>
                );
              })}

              {isLoadingMore && (
                <>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <DynamicSkeleton
                      key={index}
                      variant="rectangular"
                      dimensions={{
                        width: "100%",
                        height: cardView === "detailed" ? "360px" : "170px",
                        sx: { borderRadius: "16px" },
                      }}
                    />
                  ))}
                </>
              )}

              {!hasMore && results.length > 0 && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    textAlign: "center",
                    width: "100%",
                    color: "#666",
                    marginTop: 12,
                  }}
                >
                  <p>No more candidates to load</p>
                </div>
              )}

              <div>
                {isPopupOpen && !fallbackMonsterPopupOpen && (
                  <SidePopup
                    key={localCandidate?.id || "monster-processing"}
                    isOpen={isPopupOpen}
                    onClose={handlePopupClose}
                    title="Candidate Details"
                    sidePopupWidth={"82%"}
                  >
                    {isProcessingView ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "80vh",
                        }}
                      >
                        <VybogTALLoaderAnimation />
                      </div>
                    ) : (
                      localCandidate?.id && (
                        <CandidateDetailsContainer
                          candidate={localCandidate}
                          activeTab={0}
                          handleTabChange={() => {}}
                          hasSearched={false}
                          isEditingCandidate={isEditingCandidate}
                          handleEditCandidate={handleEditCandidate}
                          saveCandidateChanges={saveCandidateChanges}
                          resetCandidateEdit={resetCandidateEdit}
                          handleCandidateChange={handleCandidateChange}
                          editedCandidateData={editedCandidateData}
                          isVendorFeatureEnabled={isVendorFeatureEnabled}
                          vendorList={vendorList}
                          vendorOptionLoading={vendorOptionLoading}
                          handleVendorInputChange={handleVendorInputChange}
                          assignedVendor={assignedVendor}
                          hasCandidateId={hasCandidateId}
                          candidateId={realCandidateId}
                          failedDueToMissingData={isImportFailed}
                        />
                      )
                    )}
                  </SidePopup>
                )}
              </div>
            </div>

            {hasSearched && results && results.length === 0 && !isLoading && (
              <div className="monster-no-search-result">
                <h3>No candidates were found in the results of your search.</h3>
                <AnimationPlayer
                  animationSrc={NoDataFound}
                  playerHeight="250px"
                  playerWidth="250px"
                />
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

export default MonsterUsaSearch;